/** @jsxImportSource @emotion/react */
import { useTranslation } from 'react-i18next'
import React from 'react'

import { css } from '@emotion/react'
import { HexColorPicker } from 'react-colorful'
import { Col, Grid } from '../../packages/kiti/components/structure/Grid'
import { useColor } from '../../packages/kiti/components/theme/_hooks/colors'
import { useClickOutside, useKeydown } from '@btime/react-hooks'
import Input from './Input'
import Button from '../../packages/kiti/components/actions/Button'

const PopoverColorPicker = ({ value, onChange, disabled }) => {
  const { t } = useTranslation('components')
  const style = css`
    position: relative;

    & .swatch {
      width: 28px;
      height: 28px;
      border-radius: 28px;
      border: 1px solid #b9bfda;
      box-shadow: 0 0 0 1px #0000001a, inset 0 0 0 1px #0000001a;
      cursor: pointer;
    }

    & .popover {
      position: absolute;
      top: calc(100% + 8px);
      left: -10px;
      border: 1px solid #b9bfda;
      border-radius: 9px;
      box-shadow: 0 6px 12px #b9bfda;
      z-index: 999;
      padding: 10px;
      background-color: #ffffff;

      button {
        margin-top: 10px;
        width: 100%;
      }
    }

    & .popover:before {
      content: '';
      border-style: solid;
      border-width: 0px 9px 10px;
      border-color: transparent transparent #b9bfda;
      position: absolute;
      top: -11px;
      left: 15px;
    }

    & .popover:after {
      content: '';
      border-style: solid;
      border-width: 0px 9px 10px;
      border-color: transparent transparent #ffffff;
      position: absolute;
      top: -10px;
      left: 15px;
    }
  `

  const wrapperRef = React.useRef(null)
  const [isOpen, setIsOpen] = React.useState(false)
  const [color, setColor] = React.useState(value)
  const hexColor = useColor(color)

  const handleChange = value => {
    onChange && onChange(value)
    setColor(value)
  }

  const close = React.useCallback(() => setIsOpen(false), [])
  useClickOutside(wrapperRef, { action: close })
  useKeydown(wrapperRef, { keys: ['Escape'], action: close, condition: !close })

  return (
    <Grid align="center" gap={0.5} css={style}>
      <Col>
        <div
          className="swatch"
          style={{ backgroundColor: hexColor, opacity: disabled ? 0.5 : 1 }}
          onClick={() => !disabled && setIsOpen(true)}
        />
      </Col>
      <Col>
        <Input
          value={hexColor}
          onStopTyping={handleChange}
          style={{ maxWidth: '80px' }}
          disabled={disabled}
          keepValueWhenDisabled
        />
      </Col>
      {isOpen && (
        <div className="popover" ref={wrapperRef}>
          <HexColorPicker color={hexColor} onChange={handleChange} />
          <Button onClick={() => handleChange(undefined)}>{t('clear')}</Button>
        </div>
      )}
    </Grid>
  )
}

export default PopoverColorPicker
